/**
 * @name ModalDependenteDetalhe
 * @author Gabriel M. Guimarães | gabrielmg7
 * @description Modal para exibir detalhes completos de um dependente consumindo dados reais do Supabase
 * @package components/pages/gestao-financeira/dependentes/components
 */

import { Close as CloseIcon, History as HistoryIcon, Person as PersonIcon, Refresh as RefreshIcon, Settings as SettingsIcon, TrendingUp as TrendingIcon, AccountBalanceWallet as WalletIcon } from '@mui/icons-material';
import { Avatar, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogContent, DialogTitle, Grid, IconButton, LinearProgress, Tab, Tabs, Typography } from '@mui/material';
import React, { useState } from 'react';
import { GerenciarRestricoesAlimentares } from '../restricoes';
import { DependenteComSaldo } from '../useDependentesData';
import useModalDependenteDetalhe from './useModalDependenteDetalhe';

interface ModalDependenteDetalheProps {
     open: boolean;
     onClose: () => void;
     dependente: DependenteComSaldo | null;
     initialTab?: number; // índice da aba inicial (opcional)
}

interface TabPanelProps {
     children?: React.ReactNode;
     index: number;
     value: number;
}

function TabPanel(props: TabPanelProps) {
     const { children, value, index, ...other } = props;

     return (
          <div
               role='tabpanel'
               hidden={value !== index}
               id={`tabpanel-${index}`}
               aria-labelledby={`tab-${index}`}
               {...other}
          >
               {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
          </div>
     );
}

/**
 * Modal de detalhes do dependente
 * Consome dados reais do Supabase através do hook useModalDependenteDetalhe
 */
const ModalDependenteDetalhe: React.FC<ModalDependenteDetalheProps> = ({ open, onClose, dependente, initialTab = 0 }) => {
     const [tabValue, setTabValue] = useState(initialTab);

     // Hook para buscar dados reais do dependente
     const { dependenteDetalhado, loading, error, recarregarDados, formatCurrency, formatDate, getStatusColor } = useModalDependenteDetalhe(dependente);

     const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
          setTabValue(newValue);
     };

     // Sempre que o modal abrir, setar a aba inicial
     React.useEffect(() => {
          if (open) setTabValue(initialTab);
     }, [open, initialTab]);

     // Remove parênteses que contenham marcação de quantidade como "(Qtd: ...)", mantendo o restante da string
     const formatItensVendidos = (text?: string | null) => {
          if (!text) return 'Produto não informado';

          // Remove padrões entre parênteses que contenham Qtd: ... (case-insensitive)
          const cleaned = String(text)
               .replace(/\([^)]*Qtd:\s*[0-9.,]+[^)]*\)/gi, '')
               .trim();

          // Limpa espaços duplicados e parênteses vazios resultantes
          return cleaned
               .replace(/\s{2,}/g, ' ')
               .replace(/\(\s*\)/g, '')
               .trim();
     };

     const handleClose = () => {
          setTabValue(initialTab); // Reset para aba inicial ao fechar
          onClose();
     };

     if (!dependente) return null;

     // Mostrar loading enquanto busca os dados
     if (loading) {
          return (
               <Dialog
                    open={open}
                    onClose={handleClose}
                    maxWidth='lg'
                    fullWidth
                    PaperProps={{ sx: { height: '90vh' } }}
               >
                    <DialogContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                         <Box sx={{ textAlign: 'center' }}>
                              <CircularProgress size={48} />
                              <Typography
                                   variant='h6'
                                   sx={{ mt: 2 }}
                              >
                                   Carregando dados do dependente...
                              </Typography>
                         </Box>
                    </DialogContent>
               </Dialog>
          );
     }

     // Mostrar erro se houver
     if (error) {
          return (
               <Dialog
                    open={open}
                    onClose={handleClose}
                    maxWidth='lg'
                    fullWidth
                    PaperProps={{ sx: { height: '90vh' } }}
               >
                    <DialogContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                         <Box sx={{ textAlign: 'center' }}>
                              <Typography
                                   variant='h6'
                                   color='error'
                                   gutterBottom
                              >
                                   Erro ao carregar dados
                              </Typography>
                              <Typography
                                   variant='body2'
                                   color='text.secondary'
                                   gutterBottom
                              >
                                   {error}
                              </Typography>
                              <Button
                                   onClick={recarregarDados}
                                   variant='contained'
                                   sx={{ mt: 2 }}
                              >
                                   Tentar Novamente
                              </Button>
                         </Box>
                    </DialogContent>
               </Dialog>
          );
     }

     // Usar dados reais ou dados básicos do dependente
     const dadosExibicao = dependenteDetalhado || {
          ...dependente,
          dadosPessoais: null,
          comprasRecentes: [], // comentar
          extratosRecentes: [], // novo
          totalComprasRecentes: 0,
          valorMedioCompra: 0,
          restricoes: [],
          progressoGasto: 0,
          statusSaldo: 'zerado' as const,
     };

     return (
          <Dialog
               open={open}
               onClose={handleClose}
               maxWidth='lg'
               fullWidth
               PaperProps={{ sx: { height: '90vh' } }}
          >
               <DialogTitle sx={{ m: 0, p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                         <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.main' }}>
                              <PersonIcon />
                         </Avatar>
                         <Box>
                              <Typography variant='h6'>{dadosExibicao.aluno || 'Nome não informado'}</Typography>
                              <Typography
                                   variant='body2'
                                   color='text.secondary'
                              >
                                   {dadosExibicao.dadosPessoais?.email || dadosExibicao.email || ''}
                              </Typography>
                         </Box>
                         <Box sx={{ flex: 1 }} />
                         <Chip
                              label={dadosExibicao.statusSaldo?.toUpperCase() || 'INDEFINIDO'}
                              color={getStatusColor(dadosExibicao.statusSaldo)}
                              size='small'
                         />
                    </Box>
                    <IconButton
                         aria-label='close'
                         onClick={handleClose}
                         sx={{
                              position: 'absolute',
                              right: 8,
                              top: 8,
                              color: (theme) => theme.palette.grey[500],
                         }}
                    >
                         <CloseIcon />
                    </IconButton>
               </DialogTitle>

               <DialogContent
                    dividers
                    sx={{ p: 0 }}
               >
                    {/* Ação de Recarregar */}
                    <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                         <Button
                              onClick={recarregarDados}
                              startIcon={<RefreshIcon />}
                              variant='outlined'
                              size='small'
                              disabled={loading}
                         >
                              Atualizar Dados
                         </Button>
                    </Box>

                    {/* Cards de Resumo */}
                    <Box sx={{ p: 2 }}>
                         <Grid
                              container
                              spacing={2}
                         >
                              <Grid
                                   item
                                   xs={12}
                                   sm={6}
                                   md={3}
                              >
                                   <Card>
                                        <CardContent sx={{ textAlign: 'center' }}>
                                             <WalletIcon
                                                  color='primary'
                                                  sx={{ fontSize: 32, mb: 1 }}
                                             />
                                             <Typography
                                                  variant='h6'
                                                  color='primary'
                                             >
                                                  {formatCurrency(dadosExibicao.saldo_atual || dadosExibicao.saldo_disponivel || 0)}
                                             </Typography>
                                             <Typography
                                                  variant='caption'
                                                  color='text.secondary'
                                             >
                                                  Saldo Atual
                                             </Typography>
                                        </CardContent>
                                   </Card>
                              </Grid>

                              <Grid
                                   item
                                   xs={12}
                                   sm={6}
                                   md={3}
                              >
                                   <Card>
                                        <CardContent sx={{ textAlign: 'center' }}>
                                             <TrendingIcon
                                                  color='warning'
                                                  sx={{ fontSize: 32, mb: 1 }}
                                             />
                                             <Typography
                                                  variant='h6'
                                                  color='warning.main'
                                             >
                                                  {formatCurrency(dadosExibicao.valorMedioCompra || 0)}
                                             </Typography>
                                             <Typography
                                                  variant='caption'
                                                  color='text.secondary'
                                             >
                                                  Valor Médio por Compra
                                             </Typography>
                                        </CardContent>
                                   </Card>
                              </Grid>

                              <Grid
                                   item
                                   xs={12}
                                   sm={6}
                                   md={3}
                              >
                                   <Card>
                                        <CardContent sx={{ textAlign: 'center' }}>
                                             <HistoryIcon
                                                  color='info'
                                                  sx={{ fontSize: 32, mb: 1 }}
                                             />
                                             <Typography
                                                  variant='h6'
                                                  color='info.main'
                                             >
                                                  {dadosExibicao.totalComprasRecentes || 0}
                                             </Typography>
                                             <Typography
                                                  variant='caption'
                                                  color='text.secondary'
                                             >
                                                  Compras Recentes
                                             </Typography>
                                        </CardContent>
                                   </Card>
                              </Grid>

                              <Grid
                                   item
                                   xs={12}
                                   sm={6}
                                   md={3}
                              >
                                   <Card>
                                        <CardContent sx={{ textAlign: 'center' }}>
                                             <SettingsIcon
                                                  color='secondary'
                                                  sx={{ fontSize: 32, mb: 1 }}
                                             />
                                             <Typography
                                                  variant='h6'
                                                  color='secondary.main'
                                                  sx={{ minHeight: 24 }}
                                             >
                                                  {dadosExibicao.restricoes.length}
                                             </Typography>
                                             <Typography
                                                  variant='caption'
                                                  color='text.secondary'
                                             >
                                                  Restrições
                                             </Typography>
                                        </CardContent>
                                   </Card>
                              </Grid>
                         </Grid>
                    </Box>

                    {/* Tabs */}
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                         <Tabs
                              value={tabValue}
                              onChange={handleTabChange}
                              centered
                         >
                              <Tab label='Visão Geral' />
                              <Tab label='Histórico' />
                              <Tab label='Análises' />
                              <Tab label='Configurações' />
                         </Tabs>
                    </Box>

                    {/* Tab Panels */}
                    <Box sx={{ p: 2 }}>
                         <TabPanel
                              value={tabValue}
                              index={0}
                         >
                              {/* Visão Geral */}
                              <Grid
                                   container
                                   spacing={3}
                              >
                                   <Grid
                                        item
                                        xs={12}
                                        md={6}
                                   >
                                        <Card>
                                             <CardContent>
                                                  <Typography
                                                       variant='h6'
                                                       gutterBottom
                                                  >
                                                       Informações Pessoais
                                                  </Typography>
                                                  <Box sx={{ mb: 2 }}>
                                                       <Typography
                                                            variant='body2'
                                                            color='text.secondary'
                                                       >
                                                            Nome Completo
                                                       </Typography>
                                                       <Typography variant='body1'>{dadosExibicao.aluno || 'Nome não informado'}</Typography>
                                                  </Box>
                                                  <Box sx={{ mb: 2 }}>
                                                       <Typography
                                                            variant='body2'
                                                            color='text.secondary'
                                                       >
                                                            Email
                                                       </Typography>
                                                       <Typography variant='body1'>{dadosExibicao.dadosPessoais?.email || dadosExibicao.email || 'Email não informado'}</Typography>
                                                  </Box>
                                                  <Box sx={{ mb: 2 }}>
                                                       <Typography
                                                            variant='body2'
                                                            color='text.secondary'
                                                       >
                                                            Matrícula
                                                       </Typography>
                                                       <Typography variant='body1'>{dadosExibicao.dadosPessoais?.documento_numero || 'CPF não informado'}</Typography>
                                                  </Box>
                                                  <Box sx={{ mb: 2 }}>
                                                       <Typography
                                                            variant='body2'
                                                            color='text.secondary'
                                                       >
                                                            Última Atividade
                                                       </Typography>
                                                       <Typography variant='body1'>{dadosExibicao.ultimaAtividade ? formatDate(dadosExibicao.ultimaAtividade) : 'Nenhuma atividade recente'}</Typography>
                                                  </Box>
                                             </CardContent>
                                        </Card>
                                   </Grid>

                                   <Grid
                                        item
                                        xs={12}
                                        md={6}
                                   >
                                        <Card>
                                             <CardContent>
                                                  <Typography
                                                       variant='h6'
                                                       gutterBottom
                                                  >
                                                       Controle de Gastos
                                                  </Typography>
                                                  <Box sx={{ mb: 2 }}>
                                                       <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                            <Typography variant='body2'>Saldo Atual: {formatCurrency(dadosExibicao.saldo_atual || dadosExibicao.saldo_disponivel || 0)}</Typography>
                                                            <Typography
                                                                 variant='body2'
                                                                 color='text.secondary'
                                                            >
                                                                 Limite Diário: {formatCurrency(dadosExibicao.limite_diario || 0)}
                                                            </Typography>
                                                       </Box>
                                                       <LinearProgress
                                                            variant='determinate'
                                                            value={Math.min(dadosExibicao.progressoGasto || 0, 100)}
                                                            sx={{ height: 8, borderRadius: 4 }}
                                                       />
                                                       <Typography
                                                            variant='caption'
                                                            color='text.secondary'
                                                            sx={{ mt: 1, display: 'block' }}
                                                       >
                                                            {(dadosExibicao.progressoGasto || 0).toFixed(1)}% do limite diário
                                                       </Typography>
                                                  </Box>

                                                  <Box sx={{ mb: 2 }}>
                                                       <Typography
                                                            variant='body2'
                                                            color='text.secondary'
                                                       >
                                                            Limite Semanal
                                                       </Typography>
                                                       <Typography variant='body1'>{formatCurrency(dadosExibicao.limite_semanal || 0)}</Typography>
                                                  </Box>
                                             </CardContent>
                                        </Card>

                                        <Card sx={{ mt: 2 }}>
                                             <CardContent>
                                                  <Typography
                                                       variant='h6'
                                                       gutterBottom
                                                  >
                                                       Restrições Alimentares
                                                  </Typography>
                                                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                       {dadosExibicao.restricoes && dadosExibicao.restricoes.length > 0 ? (
                                                            dadosExibicao.restricoes.map((restricao, index) => (
                                                                 <Chip
                                                                      key={index}
                                                                      label={restricao}
                                                                      size='small'
                                                                      color='warning'
                                                                 />
                                                            ))
                                                       ) : (
                                                            <Typography
                                                                 variant='body2'
                                                                 color='text.secondary'
                                                            >
                                                                 Nenhuma restrição cadastrada
                                                            </Typography>
                                                       )}
                                                  </Box>
                                             </CardContent>
                                        </Card>
                                   </Grid>
                              </Grid>
                         </TabPanel>

                         <TabPanel
                              value={tabValue}
                              index={1}
                         >
                              {/* Histórico */}

                              <Card>
                                   <CardContent>
                                        <Typography
                                             variant='h6'
                                             gutterBottom
                                        >
                                             Histórico de Compras
                                        </Typography>
                                        {dadosExibicao.extratosRecentes && dadosExibicao.extratosRecentes.length > 0 ? (
                                             (() => {
                                                  // Ordena por data_venda desc se existir, depois pega os 10 primeiros
                                                  const sorted = Array.isArray(dadosExibicao.extratosRecentes)
                                                       ? [...dadosExibicao.extratosRecentes].sort((a: any, b: any) => {
                                                              const da = a?.data_venda ? new Date(a.data_venda).getTime() : 0;
                                                              const db = b?.data_venda ? new Date(b.data_venda).getTime() : 0;
                                                              return db - da;
                                                         })
                                                       : [];

                                                  let exibidos = sorted.slice(0, 10);

                                                  // Preencher com placeholders vazios se houver menos de 10 registros
                                                  if (exibidos.length < 10) {
                                                       const faltam = 10 - exibidos.length;
                                                       const placeholders = Array.from({ length: faltam }, (_, i) => ({ __placeholder: true, id: `placeholder-${i}` }));
                                                       exibidos = exibidos.concat(placeholders);
                                                  }

                                                  const somaExibidos = exibidos.reduce((acc: number, item: any) => acc + (item?.__placeholder ? 0 : Number(item?.valor_final) || 0), 0);

                                                  return (
                                                       <>
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                                 <Typography
                                                                      variant='caption'
                                                                      color='text.secondary'
                                                                 >
                                                                      Mostrando {exibidos.length} de {dadosExibicao.extratosRecentes.length} registros
                                                                 </Typography>
                                                                 <Typography
                                                                      variant='caption'
                                                                      color='text.primary'
                                                                 >
                                                                      Soma (exibidos): {formatCurrency(somaExibidos)}
                                                                 </Typography>
                                                            </Box>

                                                            <Box sx={{ maxHeight: 360, overflow: 'auto', pr: 1 }}>
                                                                 {exibidos.map((compra, index) =>
                                                                      compra?.__placeholder ? (
                                                                           <Box
                                                                                key={compra.id || index}
                                                                                sx={{
                                                                                     display: 'flex',
                                                                                     justifyContent: 'space-between',
                                                                                     alignItems: 'center',
                                                                                     py: 1,
                                                                                     borderBottom: index < exibidos.length - 1 ? '1px solid #f0f0f0' : 'none',
                                                                                }}
                                                                           >
                                                                                <Box>
                                                                                     <Typography
                                                                                          variant='body1'
                                                                                          color='text.secondary'
                                                                                     >
                                                                                          -
                                                                                     </Typography>
                                                                                     <Typography
                                                                                          variant='caption'
                                                                                          color='text.secondary'
                                                                                     >
                                                                                          -
                                                                                     </Typography>
                                                                                </Box>
                                                                                <Typography
                                                                                     variant='h6'
                                                                                     color='text.secondary'
                                                                                >
                                                                                     -
                                                                                </Typography>
                                                                           </Box>
                                                                      ) : (
                                                                           <Box
                                                                                key={compra.id || index}
                                                                                sx={{
                                                                                     display: 'flex',
                                                                                     justifyContent: 'space-between',
                                                                                     alignItems: 'center',
                                                                                     py: 1,
                                                                                     borderBottom: index < exibidos.length - 1 ? '1px solid #f0f0f0' : 'none',
                                                                                }}
                                                                           >
                                                                                <Box>
                                                                                     <Typography variant='body1'>{formatItensVendidos(compra.itens_vendidos)}</Typography>
                                                                                     <Typography
                                                                                          variant='caption'
                                                                                          color='text.secondary'
                                                                                     >
                                                                                          {formatDate(compra.data_venda)}
                                                                                     </Typography>
                                                                                </Box>
                                                                                <Typography
                                                                                     variant='h6'
                                                                                     color='error'
                                                                                >
                                                                                     -{formatCurrency(compra.valor_final || 0)}
                                                                                </Typography>
                                                                           </Box>
                                                                      ),
                                                                 )}
                                                            </Box>
                                                       </>
                                                  );
                                             })()
                                        ) : (
                                             <Typography
                                                  variant='body2'
                                                  color='text.secondary'
                                             >
                                                  Nenhuma compra registrada
                                             </Typography>
                                        )}
                                   </CardContent>
                              </Card>
                         </TabPanel>

                         <TabPanel
                              value={tabValue}
                              index={2}
                         >
                              {/* Análises */}
                              <Grid
                                   container
                                   spacing={3}
                              >
                                   <Grid
                                        item
                                        xs={12}
                                        md={6}
                                   >
                                        <Card>
                                             <CardContent>
                                                  <Typography
                                                       variant='h6'
                                                       gutterBottom
                                                  >
                                                       Estatísticas de Consumo
                                                  </Typography>
                                                  <Box sx={{ mb: 2 }}>
                                                       <Typography
                                                            variant='body2'
                                                            color='text.secondary'
                                                       >
                                                            Valor Médio por Compra
                                                       </Typography>
                                                       <Typography
                                                            variant='h6'
                                                            color='primary'
                                                       >
                                                            {formatCurrency(dadosExibicao.valorMedioCompra || 0)}
                                                       </Typography>
                                                  </Box>
                                                  <Box sx={{ mb: 2 }}>
                                                       <Typography
                                                            variant='body2'
                                                            color='text.secondary'
                                                       >
                                                            Total de Compras Recentes
                                                       </Typography>
                                                       <Typography
                                                            variant='h6'
                                                            color='info.main'
                                                       >
                                                            {dadosExibicao.totalComprasRecentes || 0} compras
                                                       </Typography>
                                                  </Box>
                                                  <Box sx={{ mb: 2 }}>
                                                       <Typography
                                                            variant='body2'
                                                            color='text.secondary'
                                                       >
                                                            Status do Saldo
                                                       </Typography>
                                                       <Chip
                                                            label={dadosExibicao.statusSaldo?.toUpperCase() || 'INDEFINIDO'}
                                                            color={getStatusColor(dadosExibicao.statusSaldo)}
                                                            sx={{ mt: 1 }}
                                                       />
                                                  </Box>
                                             </CardContent>
                                        </Card>
                                   </Grid>
                                   <Grid
                                        item
                                        xs={12}
                                        md={6}
                                   >
                                        <Card>
                                             <CardContent>
                                                  <Typography
                                                       variant='h6'
                                                       gutterBottom
                                                  >
                                                       Recomendações
                                                  </Typography>
                                                  {dadosExibicao.statusSaldo === 'zerado' && (
                                                       <Typography
                                                            variant='body2'
                                                            color='error'
                                                       >
                                                            ⚠️ Saldo zerado - Necessário recarga
                                                       </Typography>
                                                  )}
                                                  {dadosExibicao.statusSaldo === 'baixo' && (
                                                       <Typography
                                                            variant='body2'
                                                            color='warning.main'
                                                       >
                                                            💡 Saldo baixo - Considere fazer uma recarga
                                                       </Typography>
                                                  )}
                                                  {dadosExibicao.statusSaldo === 'alto' && (
                                                       <Typography
                                                            variant='body2'
                                                            color='success.main'
                                                       >
                                                            ✅ Saldo adequado
                                                       </Typography>
                                                  )}
                                                  {dadosExibicao.restricoes && dadosExibicao.restricoes.length > 0 && (
                                                       <Typography
                                                            variant='body2'
                                                            color='info.main'
                                                            sx={{ mt: 1 }}
                                                       >
                                                            🛡️ Restrições alimentares configuradas
                                                       </Typography>
                                                  )}
                                             </CardContent>
                                        </Card>
                                   </Grid>
                              </Grid>
                         </TabPanel>

                         <TabPanel
                              value={tabValue}
                              index={3}
                         >
                              {/* Configurações */}
                              <Grid
                                   container
                                   spacing={3}
                              >
                                   <Grid
                                        item
                                        xs={12}
                                        md={6}
                                   >
                                        <Card>
                                             <CardContent>
                                                  <Typography
                                                       variant='h6'
                                                       gutterBottom
                                                  >
                                                       Limites de Gastos
                                                  </Typography>
                                                  <Box sx={{ mb: 2 }}>
                                                       <Typography
                                                            variant='body2'
                                                            color='text.secondary'
                                                       >
                                                            Limite Diário
                                                       </Typography>
                                                       <Typography variant='body1'>{formatCurrency(dadosExibicao.limite_diario || 0)}</Typography>
                                                  </Box>
                                                  <Box sx={{ mb: 2 }}>
                                                       <Typography
                                                            variant='body2'
                                                            color='text.secondary'
                                                       >
                                                            Limite Semanal
                                                       </Typography>
                                                       <Typography variant='body1'>{formatCurrency(dadosExibicao.limite_semanal || 0)}</Typography>
                                                  </Box>
                                                  <Typography
                                                       variant='body2'
                                                       color='text.secondary'
                                                       sx={{ mt: 2 }}
                                                  >
                                                       💡 Para alterar os limites, entre em contato com a administração da escola
                                                  </Typography>
                                             </CardContent>
                                        </Card>
                                   </Grid>
                                   <Grid
                                        item
                                        xs={12}
                                        md={6}
                                   >
                                        {/* Gerenciamento de Restrições Alimentares */}
                                        {dadosExibicao.dadosPessoais && <GerenciarRestricoesAlimentares dependente={dadosExibicao.dadosPessoais} />}
                                   </Grid>
                              </Grid>
                         </TabPanel>
                    </Box>
               </DialogContent>
          </Dialog>
     );
};

export default ModalDependenteDetalhe;
